<!--
  Point Mapping Panel - Map SAX Slots to BMS Points

  FOR: pricing-portal-laravel/resources/js/components/sites/documentation-tabs/sax/PointMappingPanel.vue

  Maps SAX component slots to actual BMS points (BRICK integration)
  Supports auto-mapping, validation, and manual mapping
-->

<template>
  <q-dialog
    :model-value="modelValue"
    maximized
    transition-show="slide-left"
    transition-hide="slide-right"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <q-card>
      <q-toolbar class="bg-primary text-white">
        <q-toolbar-title>
          <q-icon name="link" class="q-mr-sm" />
          Point Mappings - SAX to BMS
        </q-toolbar-title>

        <q-btn flat round dense icon="close" @click="closeDialog" />
      </q-toolbar>

      <q-card-section class="q-pa-md">
        <!-- Toolbar -->
        <div class="row q-gutter-sm q-mb-md">
          <q-input
            v-model="searchQuery"
            dense
            outlined
            placeholder="Search components or slots..."
            class="col"
            clearable
          >
            <template #prepend>
              <q-icon name="search" />
            </template>
          </q-input>

          <q-select
            v-model="filterSlotType"
            :options="slotTypeOptions"
            dense
            outlined
            label="Slot Type"
            clearable
            emit-value
            map-options
            style="min-width: 150px"
          />

          <q-select
            v-model="filterMappingStatus"
            :options="mappingStatusOptions"
            dense
            outlined
            label="Status"
            clearable
            emit-value
            map-options
            style="min-width: 150px"
          />

          <q-space />

          <q-btn
            icon="auto_fix_high"
            color="secondary"
            label="Auto-Map"
            :loading="isAutoMapping"
            @click="handleAutoMap"
          >
            <q-tooltip>Automatically map slots based on naming conventions</q-tooltip>
          </q-btn>

          <q-btn
            icon="refresh"
            color="primary"
            outline
            @click="loadMappings"
          >
            <q-tooltip>Refresh</q-tooltip>
          </q-btn>
        </div>

        <!-- Stats -->
        <div class="row q-gutter-sm q-mb-md">
          <q-card flat bordered class="col">
            <q-card-section class="q-pa-sm">
              <div class="text-caption text-grey-7">Total Slots</div>
              <div class="text-h6">{{ totalSlots }}</div>
            </q-card-section>
          </q-card>

          <q-card flat bordered class="col">
            <q-card-section class="q-pa-sm">
              <div class="text-caption text-grey-7">Mapped</div>
              <div class="text-h6 text-positive">{{ mappedSlots }}</div>
            </q-card-section>
          </q-card>

          <q-card flat bordered class="col">
            <q-card-section class="q-pa-sm">
              <div class="text-caption text-grey-7">Unmapped</div>
              <div class="text-h6 text-warning">{{ unmappedSlots }}</div>
            </q-card-section>
          </q-card>

          <q-card flat bordered class="col">
            <q-card-section class="q-pa-sm">
              <div class="text-caption text-grey-7">Valid</div>
              <div class="text-h6 text-positive">{{ validMappings }}</div>
            </q-card-section>
          </q-card>

          <q-card flat bordered class="col">
            <q-card-section class="q-pa-sm">
              <div class="text-caption text-grey-7">Errors</div>
              <div class="text-h6 text-negative">{{ errorMappings }}</div>
            </q-card-section>
          </q-card>
        </div>

        <!-- Mapping Table -->
        <q-table
          :rows="filteredSlots"
          :columns="columns"
          row-key="slot_key"
          :loading="isLoading"
          :pagination="pagination"
          dense
          flat
          bordered
        >
          <template #body-cell-component="props">
            <q-td :props="props">
              <div class="text-weight-medium">{{ props.row.component_name }}</div>
              <div class="text-caption text-grey-7">{{ props.row.component_type }}</div>
            </q-td>
          </template>

          <template #body-cell-slot="props">
            <q-td :props="props">
              <q-badge
                :color="props.row.slot_type === 'input' ? 'green' : 'orange'"
                class="q-mr-xs"
              >
                {{ props.row.slot_type }}
              </q-badge>
              {{ props.row.slot_name }}
              <div v-if="props.row.slot_data_type" class="text-caption text-grey-7">
                {{ props.row.slot_data_type }}
              </div>
            </q-td>
          </template>

          <template #body-cell-mapping="props">
            <q-td :props="props">
              <div v-if="props.row.mapping">
                <div class="text-weight-medium">
                  {{ props.row.mapping.device_name }}
                </div>
                <div class="text-caption text-grey-7">
                  {{ props.row.mapping.point_name }}
                </div>
                <div v-if="props.row.mapping.brick_class" class="text-caption text-blue-7">
                  {{ props.row.mapping.brick_class }}
                </div>
              </div>
              <div v-else class="text-grey-6">
                Not mapped
              </div>
            </q-td>
          </template>

          <template #body-cell-status="props">
            <q-td :props="props">
              <q-badge
                v-if="props.row.mapping"
                :color="props.row.validation?.is_valid ? 'positive' : 'negative'"
              >
                {{ props.row.validation?.is_valid ? 'Valid' : 'Invalid' }}
              </q-badge>
              <q-badge v-else color="warning">
                Unmapped
              </q-badge>

              <q-tooltip v-if="props.row.validation && !props.row.validation.is_valid">
                {{ props.row.validation.errors.join(', ') }}
              </q-tooltip>
            </q-td>
          </template>

          <template #body-cell-actions="props">
            <q-td :props="props">
              <q-btn
                v-if="props.row.mapping"
                icon="delete"
                size="sm"
                flat
                dense
                color="negative"
                @click="deleteMapping(props.row)"
              >
                <q-tooltip>Remove mapping</q-tooltip>
              </q-btn>

              <q-btn
                v-else
                icon="add_link"
                size="sm"
                flat
                dense
                color="primary"
                @click="createMapping(props.row)"
              >
                <q-tooltip>Create mapping</q-tooltip>
              </q-btn>

              <q-btn
                v-if="props.row.mapping"
                icon="edit"
                size="sm"
                flat
                dense
                color="secondary"
                @click="editMapping(props.row)"
              >
                <q-tooltip>Edit mapping</q-tooltip>
              </q-btn>
            </q-td>
          </template>
        </q-table>
      </q-card-section>
    </q-card>

    <!-- Create/Edit Mapping Dialog -->
    <q-dialog v-model="showMappingDialog" persistent>
      <q-card style="min-width: 500px">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">
            {{ editingMapping ? 'Edit Mapping' : 'Create Mapping' }}
          </div>
          <q-space />
          <q-btn v-close-popup icon="close" flat round dense />
        </q-card-section>

        <q-card-section>
          <q-form class="q-gutter-md" @submit="saveMappingForm">
            <!-- Slot Info (readonly) -->
            <q-banner class="bg-blue-1" rounded>
              <template #avatar>
                <q-icon
                  :name="selectedSlot?.slot_type === 'input' ? 'input' : 'output'"
                  :color="selectedSlot?.slot_type === 'input' ? 'green' : 'orange'"
                />
              </template>
              <div class="text-weight-medium">
                {{ selectedSlot?.component_name }} - {{ selectedSlot?.slot_name }}
              </div>
              <div class="text-caption">
                {{ selectedSlot?.slot_type }} slot
                <span v-if="selectedSlot?.slot_data_type"> ({{ selectedSlot.slot_data_type }})</span>
              </div>
            </q-banner>

            <!-- Device Selection -->
            <q-select
              v-model="mappingForm.device_id"
              :options="deviceOptions"
              label="Device *"
              dense
              outlined
              emit-value
              map-options
              option-value="id"
              option-label="name"
              :rules="[val => !!val || 'Device is required']"
              @update:model-value="loadPointsForDevice"
            >
              <template #prepend>
                <q-icon name="router" />
              </template>
            </q-select>

            <!-- Point Selection -->
            <q-select
              v-model="mappingForm.point_id"
              :options="pointOptions"
              label="Point *"
              dense
              outlined
              emit-value
              map-options
              option-value="id"
              option-label="name"
              :rules="[val => !!val || 'Point is required']"
              :disable="!mappingForm.device_id"
              :loading="isLoadingPoints"
            >
              <template #prepend>
                <q-icon name="sensors" />
              </template>

              <template #option="scope">
                <q-item v-bind="scope.itemProps">
                  <q-item-section>
                    <q-item-label>{{ scope.opt.name }}</q-item-label>
                    <q-item-label caption>
                      {{ scope.opt.brick_class || 'Unknown type' }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </template>
            </q-select>

            <!-- BRICK Class (optional) -->
            <q-input
              v-model="mappingForm.brick_class"
              label="BRICK Class (optional)"
              dense
              outlined
            >
              <template #prepend>
                <q-icon name="category" />
              </template>
            </q-input>
          </q-form>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn v-close-popup label="Cancel" flat />
          <q-btn
            label="Save"
            color="primary"
            :loading="isSavingMapping"
            @click="saveMappingForm"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </q-dialog>
</template>

<script setup>
  import { ref, computed, watch, onMounted } from 'vue';
  import { useQuasar } from 'quasar';
  import { useSaxEditorStore } from '@/stores/saxEditor';
  import { storeToRefs } from 'pinia';
  import axios from 'axios';

  // Props
  const props = defineProps({
    modelValue: {
      type: Boolean,
      required: true,
    },
    equipmentId: {
      type: Number,
      required: true,
    },
    siteId: {
      type: Number,
      default: null,
    },
  });

  // Emits
  const emit = defineEmits(['update:modelValue']);

  // Quasar
  const $q = useQuasar();

  // Store
  const saxStore = useSaxEditorStore();
  const { currentSaxData } = storeToRefs(saxStore);

  // State
  const mappings = ref([]);
  const slots = ref([]);
  const devices = ref([]);
  const points = ref([]);
  const isLoading = ref(false);
  const isAutoMapping = ref(false);
  const isSavingMapping = ref(false);
  const isLoadingPoints = ref(false);
  const searchQuery = ref('');
  const filterSlotType = ref(null);
  const filterMappingStatus = ref(null);
  const showMappingDialog = ref(false);
  const selectedSlot = ref(null);
  const editingMapping = ref(null);

  // Form
  const mappingForm = ref({
    device_id: null,
    point_id: null,
    brick_class: '',
  });

  // Constants
  const slotTypeOptions = [
    { label: 'All Types', value: null },
    { label: 'Input', value: 'input' },
    { label: 'Output', value: 'output' },
  ];

  const mappingStatusOptions = [
    { label: 'All Status', value: null },
    { label: 'Mapped', value: 'mapped' },
    { label: 'Unmapped', value: 'unmapped' },
    { label: 'Valid', value: 'valid' },
    { label: 'Invalid', value: 'invalid' },
  ];

  // Table columns
  const columns = [
    {
      name: 'component',
      label: 'Component',
      align: 'left',
      field: 'component_name',
      sortable: true,
    },
    {
      name: 'slot',
      label: 'Slot',
      align: 'left',
      field: 'slot_name',
      sortable: true,
    },
    {
      name: 'mapping',
      label: 'Mapped To',
      align: 'left',
    },
    {
      name: 'status',
      label: 'Status',
      align: 'center',
    },
    {
      name: 'actions',
      label: 'Actions',
      align: 'center',
    },
  ];

  const pagination = ref({
    rowsPerPage: 50,
  });

  // Computed

  const deviceOptions = computed(() => {
    return devices.value;
  });

  const pointOptions = computed(() => {
    return points.value;
  });

  const filteredSlots = computed(() => {
    let filtered = slots.value;

    // Search filter
    if (searchQuery.value) {
      const query = searchQuery.value.toLowerCase();
      filtered = filtered.filter((slot) =>
        slot.component_name.toLowerCase().includes(query) ||
        slot.slot_name.toLowerCase().includes(query) ||
        slot.mapping?.device_name?.toLowerCase().includes(query) ||
        slot.mapping?.point_name?.toLowerCase().includes(query),
      );
    }

    // Slot type filter
    if (filterSlotType.value) {
      filtered = filtered.filter((slot) => slot.slot_type === filterSlotType.value);
    }

    // Mapping status filter
    if (filterMappingStatus.value) {
      if (filterMappingStatus.value === 'mapped') {
        filtered = filtered.filter((slot) => slot.mapping);
      } else if (filterMappingStatus.value === 'unmapped') {
        filtered = filtered.filter((slot) => !slot.mapping);
      } else if (filterMappingStatus.value === 'valid') {
        filtered = filtered.filter((slot) => slot.mapping && slot.validation?.is_valid);
      } else if (filterMappingStatus.value === 'invalid') {
        filtered = filtered.filter((slot) => slot.mapping && !slot.validation?.is_valid);
      }
    }

    return filtered;
  });

  const totalSlots = computed(() => slots.value.length);
  const mappedSlots = computed(() => slots.value.filter((s) => s.mapping).length);
  const unmappedSlots = computed(() => slots.value.filter((s) => !s.mapping).length);
  const validMappings = computed(() => slots.value.filter((s) => s.mapping && s.validation?.is_valid).length);
  const errorMappings = computed(() => slots.value.filter((s) => s.mapping && !s.validation?.is_valid).length);

  // Methods

  /**
   * Load mappings and devices
   */
  async function loadMappings() {
    isLoading.value = true;
    try {
      // Load mappings
      const apiPath = props.siteId
        ? `/v2/sites/${props.siteId}/equipment/${props.equipmentId}/documentation/sax/point-mappings`
        : `/equipment/${props.equipmentId}/documentation/sax/point-mappings`;

      const response = await axios.get(apiPath);
      mappings.value = response.data.mappings || [];

      // Build slots list from SAX data
      buildSlotsList();

      // Load devices
      await loadDevices();
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to load mappings',
        caption: error.message,
      });
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Build slots list from SAX data
   */
  function buildSlotsList() {
    const slotsList = [];

    if (!currentSaxData.value?.components) {
      slots.value = [];
      return;
    }

    currentSaxData.value.components.forEach((component) => {
      // Input slots
      (component.input_slots || []).forEach((slot) => {
        const mapping = mappings.value.find(
          (m) => m.sax_component_id === component.id && m.sax_slot_name === slot.name,
        );

        slotsList.push({
          slot_key: `${component.id}-${slot.name}`,
          component_id: component.id,
          component_name: component.name,
          component_type: component.type,
          slot_name: slot.name,
          slot_type: 'input',
          slot_data_type: slot.type,
          mapping: mapping,
          validation: mapping?.validation,
        });
      });

      // Output slots
      (component.output_slots || []).forEach((slot) => {
        const mapping = mappings.value.find(
          (m) => m.sax_component_id === component.id && m.sax_slot_name === slot.name,
        );

        slotsList.push({
          slot_key: `${component.id}-${slot.name}`,
          component_id: component.id,
          component_name: component.name,
          component_type: component.type,
          slot_name: slot.name,
          slot_type: 'output',
          slot_data_type: slot.type,
          mapping: mapping,
          validation: mapping?.validation,
        });
      });
    });

    slots.value = slotsList;
  }

  /**
   * Load devices for equipment
   */
  async function loadDevices() {
    try {
      // Assuming devices API exists
      const apiPath = props.siteId
        ? `/v2/sites/${props.siteId}/equipment/${props.equipmentId}/devices`
        : `/equipment/${props.equipmentId}/devices`;

      const response = await axios.get(apiPath);
      devices.value = response.data.data || [];
    } catch (error) {
      console.error('Failed to load devices:', error);
    }
  }

  /**
   * Load points for device
   */
  async function loadPointsForDevice(deviceId) {
    if (!deviceId) {
      points.value = [];
      return;
    }

    isLoadingPoints.value = true;
    try {
      const response = await axios.get(`/devices/${deviceId}/points`);
      points.value = response.data.data || [];
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to load points',
        caption: error.message,
      });
    } finally {
      isLoadingPoints.value = false;
    }
  }

  /**
   * Auto-map slots to points
   */
  async function handleAutoMap() {
    isAutoMapping.value = true;
    try {
      const apiPath = props.siteId
        ? `/v2/sites/${props.siteId}/equipment/${props.equipmentId}/documentation/sax/point-mappings/auto-map`
        : `/equipment/${props.equipmentId}/documentation/sax/point-mappings/auto-map`;

      const response = await axios.post(apiPath);

      $q.notify({
        type: 'positive',
        message: 'Auto-mapping complete',
        caption: `${response.data.mapped_count} slots mapped`,
      });

      loadMappings();
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Auto-mapping failed',
        caption: error.message,
      });
    } finally {
      isAutoMapping.value = false;
    }
  }

  /**
   * Create mapping
   */
  function createMapping(slot) {
    selectedSlot.value = slot;
    editingMapping.value = null;

    mappingForm.value = {
      device_id: null,
      point_id: null,
      brick_class: '',
    };

    showMappingDialog.value = true;
  }

  /**
   * Edit mapping
   */
  function editMapping(slot) {
    selectedSlot.value = slot;
    editingMapping.value = slot.mapping;

    mappingForm.value = {
      device_id: slot.mapping.device_id,
      point_id: slot.mapping.point_id,
      brick_class: slot.mapping.brick_class || '',
    };

    // Load points for device
    loadPointsForDevice(slot.mapping.device_id);

    showMappingDialog.value = true;
  }

  /**
   * Save mapping form
   */
  async function saveMappingForm() {
    if (!mappingForm.value.device_id || !mappingForm.value.point_id) {
      $q.notify({
        type: 'negative',
        message: 'Please select both device and point',
      });
      return;
    }

    isSavingMapping.value = true;
    try {
      const apiPath = props.siteId
        ? `/v2/sites/${props.siteId}/equipment/${props.equipmentId}/documentation/sax/point-mappings`
        : `/equipment/${props.equipmentId}/documentation/sax/point-mappings`;

      const data = {
        sax_component_id: selectedSlot.value.component_id,
        sax_slot_name: selectedSlot.value.slot_name,
        sax_slot_type: selectedSlot.value.slot_type,
        device_id: mappingForm.value.device_id,
        point_id: mappingForm.value.point_id,
        brick_class: mappingForm.value.brick_class || null,
      };

      if (editingMapping.value) {
        await axios.put(`${apiPath}/${editingMapping.value.id}`, data);
      } else {
        await axios.post(apiPath, data);
      }

      $q.notify({
        type: 'positive',
        message: `Mapping ${editingMapping.value ? 'updated' : 'created'}`,
      });

      showMappingDialog.value = false;
      loadMappings();
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to ${editingMapping.value ? 'update' : 'create'} mapping`,
        caption: error.response?.data?.message || error.message,
      });
    } finally {
      isSavingMapping.value = false;
    }
  }

  /**
   * Delete mapping
   */
  function deleteMapping(slot) {
    $q.dialog({
      title: 'Delete Mapping',
      message: `Remove mapping for ${slot.component_name} - ${slot.slot_name}?`,
      cancel: true,
      persistent: true,
    }).onOk(async () => {
      try {
        const apiPath = props.siteId
          ? `/v2/sites/${props.siteId}/equipment/${props.equipmentId}/documentation/sax/point-mappings/${slot.mapping.id}`
          : `/equipment/${props.equipmentId}/documentation/sax/point-mappings/${slot.mapping.id}`;

        await axios.delete(apiPath);

        $q.notify({
          type: 'positive',
          message: 'Mapping deleted',
        });

        loadMappings();
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: 'Failed to delete mapping',
          caption: error.message,
        });
      }
    });
  }

  /**
   * Close dialog
   */
  function closeDialog() {
    emit('update:modelValue', false);
  }

  // Watchers

  watch(
    () => props.modelValue,
    (newValue) => {
      if (newValue) {
        loadMappings();
      }
    },
  );

  watch(
    () => currentSaxData.value,
    () => {
      if (props.modelValue) {
        buildSlotsList();
      }
    },
    { deep: true },
  );
</script>

<style scoped lang="scss">
// Add any custom styles here
</style>
